import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  Grid,
  Paper,
  Stack,
  Typography,
  FormControl,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Dialog,
  DialogContent,
} from "@mui/material";
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import WorkIcon from '@mui/icons-material/Work';
import DescriptionIcon from '@mui/icons-material/Description';
import PersonIcon from '@mui/icons-material/Person';
import ReceiptIcon from '@mui/icons-material/Receipt';
import FolderIcon from '@mui/icons-material/Folder';
import { StyledInput } from "../StyledComponents/StyledComponent";
import { useAllEmployeeDetails, useUploadDocument, useGetSingleEmployeeDetails } from "../../hooks/useEmployees";
import { styled } from '@mui/system';
import { getMediaUrl } from "../../utils/urlHelper";
import { useAlert } from "../../context/AlertContext";

// ---- FORTHEYE soft-card tokens (shared with the employee portal) ----
const CARD_SX = {
  p: { xs: 2, sm: 2.5, md: 3 },
  borderRadius: '22px',
  bgcolor: '#fff',
  border: '1px solid #eef0f4',
  boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};
const ACCENT = '#2f6bff';

const DocumentCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  padding: '16px',
  background: '#fff',
  border: '1px solid #eef0f4',
  boxShadow: 'none',
  transition: 'all 0.2s ease',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    borderColor: `${ACCENT}55`,
    boxShadow: '0 8px 22px rgba(17,24,39,0.07)',
    transform: 'translateY(-3px)'
  },
}));

const PreviewPlaceholder = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100px',
  backgroundColor: '#f8fafc',
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '12px',
  border: '1px solid #eef0f4',
  overflow: 'hidden',
  position: 'relative',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  '&:hover .zoom-overlay': {
    opacity: 1,
  }
}));

const ZoomOverlay = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(0,0,0,0.3)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  opacity: 0,
  transition: 'opacity 0.2s ease',
  cursor: 'pointer',
});

const DocumentLink = styled('a')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  color: ACCENT,
  textDecoration: 'none',
  padding: '6px 10px',
  borderRadius: '12px',
  fontSize: '0.8rem',
  transition: 'all 0.2s ease',
  backgroundColor: `${ACCENT}14`,
  '&:hover': {
    backgroundColor: `${ACCENT}22`,
  },
}));

const EmpDocument = () => {
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedFileType, setSelectedFileType] = useState("");
  const [previewImage, setPreviewImage] = useState(null);
  const { data: empdata } = useAllEmployeeDetails();
  const { data: fullEmployeeData, isLoading: isDocsLoading } = useGetSingleEmployeeDetails(selectedEmployee?.employee_no);
  const { mutate: uploadDoc } = useUploadDocument();
  const { showSuccess, showError } = useAlert();
  const [searchquery, setSearchQuery] = useState([]);

  const handleSearch = (e) => {
    const value = e.target.value.trim();
    // Typing a new query drops any current selection, so the results list (and
    // the empty right-hand panel) reappear immediately. Previously the selected
    // employee stayed pinned behind the "Change" card until the user clicked it,
    // making a fresh search silently do nothing.
    if (selectedEmployee) setSelectedEmployee("");
    if (value === "") {
      setSearchQuery([]);
    } else {
      const response = empdata?.filter((emp) =>
        emp.name.toLowerCase().includes(value.toLowerCase())
      );
      setSearchQuery(response || []);
    }
  };

  const handleSelect = (emp) => {
    setSelectedEmployee(emp);
  };

  const renderDocumentGrid = () => {
    if (!selectedEmployee) return null;

    const documentCategories = [
      {
        group: 'Identity',
        items: [
          { key: 'aadhar_card', label: 'Aadhar Card', icon: <PersonIcon /> },
          { key: 'pan_card', label: 'PAN Card', icon: <PersonIcon /> }
        ]
      },
      {
        group: 'Employment',
        items: [
          { key: 'offer_letter', label: 'Offer Letter', icon: <WorkIcon /> },
          { key: 'resignation_acceptance_letter', label: 'Resignation Letter', icon: <WorkIcon /> },
          { key: 'experience_letter', label: 'Experience Letter', icon: <WorkIcon /> },
          { key: 'relieving_letter', label: 'Relieving Letter', icon: <WorkIcon /> },
          { key: 'resume', label: 'Resume', icon: <InsertDriveFileIcon /> }
        ]
      },
      {
        group: 'Education',
        items: [
          { key: 'sslc_certificate', label: 'SSLC Certificate', icon: <DescriptionIcon /> },
          { key: 'intermediate_certificate', label: 'Intermediate Certificate', icon: <DescriptionIcon /> },
          { key: 'graduation_certificate', label: 'Graduation Certificate', icon: <DescriptionIcon /> },
          { key: 'pg_certificate', label: 'PG Certificate', icon: <DescriptionIcon /> },
          { key: 'nicsm_certificate', label: 'NISM Certificate', icon: <DescriptionIcon /> }
        ]
      },
      {
        group: 'Finance',
        items: [
          { key: 'bank_statement_last_6_months', label: 'Bank Statement', icon: <AccountBalanceIcon /> },
          { key: 'pay_slips_last_6_months', label: 'Pay Slips', icon: <ReceiptIcon /> }
        ]
      }
    ];

    const documentTypes = documentCategories.flatMap(cat => cat.items);
    const currentDocs = fullEmployeeData?.documents?.[0] || {};

    return (
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 800, mb: 3, display: 'flex', alignItems: 'center', gap: 1, color: '#111827' }}>
          <DescriptionIcon sx={{ color: ACCENT }} />
          Uploaded Documents
        </Typography>

        {isDocsLoading ? (
          <Stack alignItems="center" spacing={1.5} sx={{ py: 6 }}>
            <Box sx={{ width: 56, height: 56, borderRadius: '16px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <DescriptionIcon />
            </Box>
            <Typography sx={{ color: '#6b7280', fontWeight: 600 }}>Loading documents...</Typography>
          </Stack>
        ) : (
          <Grid container spacing={2}>
            {documentTypes.map((item) => {
              const docPath = currentDocs[item.key];
              const isUploaded = !!docPath;
              const fileUrl = getMediaUrl(docPath);
              const isImage = docPath && (docPath.toLowerCase().endsWith('.jpg') || docPath.toLowerCase().endsWith('.jpeg') || docPath.toLowerCase().endsWith('.png'));

              return (
                <Grid item xs={12} sm={6} md={3} key={item.key}>
                  <DocumentCard sx={{
                    border: isUploaded ? '1px solid #16a34a33' : '1px dashed #e5e7eb',
                    backgroundColor: isUploaded ? '#f0fdf4' : '#fff',
                    opacity: selectedFileType && selectedFileType !== item.key ? 0.5 : 1
                  }}>
                    <PreviewPlaceholder>
                      {isUploaded ? (
                        isImage ? (
                          <>
                            <img src={fileUrl} alt={item.label} />
                            <ZoomOverlay className="zoom-overlay" onClick={() => setPreviewImage(fileUrl)}>
                              <ZoomInIcon />
                            </ZoomOverlay>
                          </>
                        ) : (
                          <Box sx={{ width: 44, height: 44, borderRadius: '12px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <InsertDriveFileIcon />
                          </Box>
                        )
                      ) : (
                        <Box sx={{ textAlign: 'center', color: '#9ca3af' }}>
                          {item.icon}
                          <Typography variant="caption" display="block">Missing</Typography>
                        </Box>
                      )}
                    </PreviewPlaceholder>

                    <Typography variant="caption" sx={{ fontWeight: 700, mb: 1, flexGrow: 1, fontSize: '0.75rem', color: '#111827' }}>
                      {item.label}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
                      {isUploaded && (
                        <DocumentLink href={fileUrl} target="_blank" rel="noopener noreferrer">
                          <VisibilityIcon sx={{ fontSize: '1rem' }} />
                        </DocumentLink>
                      )}
                      <Button
                        variant={isUploaded ? "outlined" : "contained"}
                        size="small"
                        disableElevation
                        onClick={() => {
                          setSelectedFileType(item.key);
                          document.getElementById('util-file-input').click();
                        }}
                        sx={{
                          flexGrow: 1,
                          fontSize: '0.7rem',
                          textTransform: 'none',
                          fontWeight: 700,
                          height: '28px',
                          borderRadius: '12px',
                          ...(isUploaded
                            ? { color: ACCENT, borderColor: '#e5e7eb', '&:hover': { borderColor: ACCENT, backgroundColor: `${ACCENT}0d` } }
                            : {
                                color: '#fff',
                                background: 'linear-gradient(135deg,#3b82f6,#2f6bff)',
                                '&:hover': { background: 'linear-gradient(135deg,#2f6bff,#2557d6)' },
                              }),
                        }}
                      >
                        {isUploaded ? "Replace" : "Upload"}
                      </Button>
                    </Box>
                  </DocumentCard>
                </Grid>
              );
            })}
          </Grid>
        )}

        <input
          type="file"
          id="util-file-input"
          hidden
          onChange={(e) => {
            const file = e.target.files[0];
            if (file) {
              if (file.size > 5 * 1024 * 1024) {
                showError("File size exceeds 5MB limit.");
                return;
              }
              uploadDoc({
                employee_no: selectedEmployee.employee_no,
                file_type: selectedFileType,
                file: file
              }, {
                onSuccess: () => {
                  showSuccess("Document uploaded successfully!");
                  setSelectedFileType("");
                }
              });
            }
          }}
          accept="image/jpeg, image/png, application/pdf"
        />

        <Dialog open={!!previewImage} onClose={() => setPreviewImage(null)} maxWidth="lg" PaperProps={{ sx: { borderRadius: '24px' } }}>
          <DialogContent sx={{ p: 0, position: 'relative' }}>
            <Button
              onClick={() => setPreviewImage(null)}
              sx={{ position: 'absolute', top: 10, right: 10, color: '#fff', textTransform: 'none', fontWeight: 700, borderRadius: '12px', backgroundColor: 'rgba(0,0,0,0.5)', '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' } }}
            >
              Close
            </Button>
            <img src={previewImage} alt="Large Preview" style={{ width: '100%', display: 'block' }} />
          </DialogContent>
        </Dialog>
      </Box>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Header card */}
      <Paper elevation={0} sx={{ ...CARD_SX, mb: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ width: 52, height: 52, borderRadius: '16px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <FolderIcon />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.35rem', md: '1.7rem' }, color: '#111827', letterSpacing: '-0.02em', lineHeight: 1.15 }}>
              Document Repository
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              Search an employee and manage their uploaded documents.
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Grid container spacing={{ xs: 2, md: 3 }}>
        <Grid item xs={12} md={4}>
          {/* Search / select card */}
          <Paper elevation={0} sx={CARD_SX}>
            <FormControl fullWidth>
              <StyledInput fullWidth placeholder="Search Employee..." onChange={handleSearch} />
            </FormControl>

            <Box sx={{ mt: 2 }}>
              {!selectedEmployee ? (
                <Box sx={{ p: 2, borderRadius: '16px', border: '1px solid #eef0f4', bgcolor: '#fff', maxHeight: 300, overflowY: "auto" }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Results ({searchquery.length})
                  </Typography>
                  <List dense>
                    {searchquery.map((emp, index) => (
                      <ListItem
                        key={index}
                        button
                        onClick={() => handleSelect(emp)}
                        sx={{
                          borderRadius: '12px',
                          mb: 0.5,
                          "&:hover": { backgroundColor: `${ACCENT}0d` },
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar src={emp.avatar} alt={emp.name} sx={{ width: 32, height: 32 }} />
                        </ListItemAvatar>
                        <ListItemText
                          primary={<Typography variant="body2" sx={{ fontWeight: 700 }}>{emp.name}</Typography>}
                          secondary={<Typography variant="caption">{emp.employee_no}</Typography>}
                        />
                      </ListItem>
                    ))}
                    {searchquery.length === 0 && (
                      <Typography variant="caption" sx={{ p: 2, display: 'block', textAlign: 'center', color: '#6b7280' }}>
                        Type to search...
                      </Typography>
                    )}
                  </List>
                </Box>
              ) : (
                <Box sx={{ p: 2, borderRadius: '16px', border: `1px solid ${ACCENT}33`, display: "flex", alignItems: "center", bgcolor: `${ACCENT}0a` }}>
                  <Avatar src={selectedEmployee.avatar} sx={{ width: 48, height: 48, mr: 2 }} />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: ACCENT }}>
                      {selectedEmployee.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Emp ID: {selectedEmployee.employee_no}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    onClick={() => setSelectedEmployee("")}
                    sx={{ ml: 'auto', minWidth: 0, color: '#6b7280', textTransform: 'none', fontWeight: 700, borderRadius: '12px' }}
                  >
                    Change
                  </Button>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          {selectedEmployee ? (
            <Paper elevation={0} sx={CARD_SX}>
              {renderDocumentGrid()}
            </Paper>
          ) : (
            <Paper elevation={0} sx={{ ...CARD_SX, height: '100%', minHeight: 400, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
              <Box sx={{ width: 64, height: 64, borderRadius: '18px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <PersonIcon sx={{ fontSize: 34 }} />
              </Box>
              <Typography variant="body1" sx={{ color: '#6b7280', fontWeight: 600 }}>Select an employee to manage documents</Typography>
            </Paper>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default EmpDocument;
